import { Helmet } from "react-helmet-async";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
// @mui
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
// components
import Iconify from "../components/iconify";
// mock
// import FileManager from "../sections/@dashboard/files/FileManager";
import { setDialog } from "../features/dialog/dialogSlice";
import FileDataTable from "../sections/@dashboard/files/FileDataTable/FileDataTable";
import { useGetFileDatasQuery } from "../features/fileData/fileDataApiSlice";
import Loading from "../components/spinners/Loading";
import ReadingConfirmationOverview from "../sections/@dashboard/files/ReadingConfirmation/ReadingConfirmationOverview";
import { NavLink, Route, Routes, useLocation } from "react-router-dom";
import PreviewDocumentView from "../sections/@dashboard/files/PreviewDocumentView";
import useAuth from "../hooks/useAuth";
import CreateNewDocumentView from "../sections/@dashboard/files/CreateNewDocumentView";
import UploadDocumentView from "../sections/@dashboard/files/UploadDocumentView";
import CreateQMStructure from "../sections/@dashboard/files/CreateQMStructure";
import FolderView from "../sections/@dashboard/files/FolderView/FolderView";
import {
  useGetQMExportStatusQuery,
  useLazyDownloadQMExportQuery,
  useStartQMExportMutation,
} from "../features/fileManager/fileManagerApiSlice";

// ----------------------------------------------------------------------

// ----------------------------------------------------------------------

export default function FilesPage() {
  const { isAdmin, isSuperAdmin } = useAuth();
  let location = useLocation();
  const {
    data: fileDatas = [],
    isError: isFileDataError,
    isLoading,
  } = useGetFileDatasQuery();

  const dispatch = useDispatch();

  const handleOpenDeleteQMStructureDialog = () => {
    dispatch(
      setDialog({
        title: "QM Dokumentenstruktur löschen",
        type: "DELETE",
        content: "DeleteQMFolderStructure",
      })
    );
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <Helmet>
        <title> Digit QM | QM Dokumente </title>
      </Helmet>

      <Container sx={{ minHeight: "100%" }} maxWidth="">
        {location?.pathname !== "/dashboard/files" ? (
          <Button
            component={NavLink}
            to={"/dashboard/files"}
            // onClick={() => handleChangeMenuState("/dashboard/files")}
            variant="contained"
            color="warning"
            sx={{ minWidth: 300, mb: 2 }}
            startIcon={<Iconify icon="mdi:arrow-left" />}
          >
            MENÜ
          </Button>
        ) : (
          <Typography variant="h4" gutterBottom>
            QM-Dokumente
          </Typography>
        )}

        <Routes>
          <Route
            index
            element={
              <FilesMainMenu
                isAdmin={isAdmin}
                isSuperAdmin={isSuperAdmin}
                handleOpenDeleteQMStructureDialog={
                  handleOpenDeleteQMStructureDialog
                }
              />
            }
          />
          <Route
            path="overview/:searchId"
            element={
              <>
                {isFileDataError ? (
                  <Alert severity="error">
                    Dokumente konnten nicht geladen werden.
                  </Alert>
                ) : fileDatas.length > 0 ? (
                  <FileDataTable fileDatas={fileDatas} />
                ) : (
                  <Alert severity="info">Noch keine Dokumenteneinträge</Alert>
                )}
              </>
            }
          />

          <Route path="folderview" element={<FolderView />} />

          {isAdmin && (
            <Route path="reading" element={<ReadingConfirmationOverview />} />
          )}
          {isSuperAdmin && (
            <Route path="create-qm" element={<CreateQMStructure />} />
          )}
          <Route path="show/:fileId" element={<PreviewDocumentView />} />
          <Route path="new" element={<CreateNewDocumentView />} />
          <Route path="upload" element={<UploadDocumentView />} />
        </Routes>
      </Container>
    </>
  );
}

function FilesMainMenu({
  isAdmin,
  isSuperAdmin,
  handleOpenDeleteQMStructureDialog,
}) {
  const [exportDialog, setExportDialog] = useState({
    open: false,
  });

  const handleOpenQMExportDialog = () => {
    setExportDialog({
      open: true,
    });
  };

  const handleCloseQMExportDialog = () => {
    setExportDialog({
      open: false,
    });
  };

  return (
    <Stack
      direction="column"
      alignItems="center"
      justifyContent="space-between"
      sx={{ minHeight: "100%", py: 3 }}
    >
      <Button
        component={NavLink}
        to={"overview/show"}
        variant="contained"
        sx={{ minWidth: 300, my: 3 }}
        startIcon={<Iconify icon="gala:menu-left" />}
      >
        Dokumentenübersicht
      </Button>
      <Button
        component={NavLink}
        to={"folderview"}
        variant="contained"
        sx={{ minWidth: 300, my: 3 }}
        startIcon={<Iconify icon="mdi:folders-outline" />}
      >
        Ordneransicht
      </Button>
      <Button
        component={NavLink}
        to={"new"}
        variant="contained"
        sx={{ minWidth: 300, my: 3 }}
        startIcon={<Iconify icon="mdi:file-document-plus-outline" />}
      >
        Neues Dokument
      </Button>
      <Button
        component={NavLink}
        to={"upload"}
        variant="contained"
        sx={{ minWidth: 300, my: 3 }}
        startIcon={<Iconify icon="grommet-icons:document-upload" />}
      >
        Dokument hochladen
      </Button>
      {isAdmin && (
        <Button
          component={NavLink}
          to={"reading"}
          variant="contained"
          sx={{ minWidth: 300, my: 3 }}
          startIcon={<Iconify icon="mdi:book-check-outline" />}
        >
          Lesebestätigungen
        </Button>
      )}
      {isSuperAdmin && (
        <>
          <Button
            onClick={handleOpenQMExportDialog}
            variant="contained"
            sx={{ minWidth: 300, my: 3 }}
            startIcon={<Iconify icon="mdi:archive-arrow-down-outline" />}
          >
            Export QM Struktur
          </Button>

          <QMFilesExportDialog
            open={exportDialog.open}
            onClose={handleCloseQMExportDialog}
          />

          <Button
            component={NavLink}
            to={"create-qm"}
            variant="contained"
            sx={{ minWidth: 300, my: 3 }}
            startIcon={<Iconify icon="lets-icons:folders-line" />}
          >
            QM Struktur erstellen
          </Button>

          <Button
            onClick={handleOpenDeleteQMStructureDialog}
            color="error"
            sx={{ minWidth: 300, my: 3 }}
            variant="contained"
            startIcon={<Iconify icon="tabler:folders-off" />}
          >
            ORDNERSTRUKTUR LÖSCHEN
          </Button>
        </>
      )}
    </Stack>
  );
}

function getExportStep(status) {
  if (status === "ready" || status === "error") return 3;
  if (status === "zipping") return 2;
  if (status === "queued" || status === "preparing") return 1;
  return 0;
}

function formatBytes(bytes) {
  if (!bytes) return "0 MB";

  const units = ["Bytes", "KB", "MB", "GB"];
  const unitIndex = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  );
  const value = bytes / 1024 ** unitIndex;

  return `${value.toLocaleString("de-DE", {
    maximumFractionDigits: unitIndex === 0 ? 0 : 1,
  })} ${units[unitIndex]}`;
}

function QMFilesExportDialog({ open, onClose }) {
  const [exportJob, setExportJob] = useState(null);
  const [startQMExport, { isLoading: isStarting }] = useStartQMExportMutation();
  const [downloadQMExport, { isFetching: isDownloading }] =
    useLazyDownloadQMExportQuery();

  const exportId = exportJob?.exportId;
  const shouldPoll =
    Boolean(exportId) && !["ready", "error"].includes(exportJob?.status);

  const { data: statusData, isFetching: isPolling } = useGetQMExportStatusQuery(
    exportId,
    {
      skip: !shouldPoll,
      pollingInterval: 1500,
    }
  );

  useEffect(() => {
    if (statusData) {
      setExportJob(statusData);
    }
  }, [statusData]);

  const steps = useMemo(
    () => ["Start", "Vorbereitung", "ZIP erstellen", "Download"],
    []
  );

  const startExport = useCallback(async () => {
    setExportJob(null);

    try {
      const result = await startQMExport().unwrap();
      setExportJob(result);
    } catch (error) {
      setExportJob({
        status: "error",
        progress: 100,
        message:
          error?.data?.message || "Der Export konnte nicht gestartet werden.",
      });
    }
  }, [startQMExport]);

  useEffect(() => {
    if (!open) {
      setExportJob(null);
    }
  }, [open]);

  const handleDownloadExport = async () => {
    try {
      const blob = await downloadQMExport(exportJob.exportId).unwrap();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = exportJob.fileName || "QM-Dateien.zip";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setExportJob((currentJob) => ({
        ...currentJob,
        status: "error",
        progress: 100,
        message:
          error?.data?.message || "Der Download konnte nicht gestartet werden.",
      }));
    }
  };

  const activeStep = getExportStep(exportJob?.status);
  const progressValue = exportJob?.progress || 0;
  const isWorking = isStarting || shouldPoll;
  const exportStarted = Boolean(exportJob) || isStarting;
  const exportReady = exportJob?.status === "ready";
  const exportFailed = exportJob?.status === "error";

  return (
    <Dialog
      open={open}
      onClose={isWorking ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                display: "grid",
                placeItems: "center",
                width: 44,
                height: 44,
                borderRadius: 1.5,
                bgcolor: "primary.lighter",
                color: "primary.main",
              }}
            >
              <Iconify icon="mdi:archive-arrow-down-outline" width={26} />
            </Box>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" mb={0.25}>
                <Typography variant="h6">Export QM Struktur</Typography>
                <Chip
                  size="small"
                  color="error"
                  label="SuperAdmin"
                  sx={{ fontWeight: 700 }}
                />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Alle QM Ordner und Dateien des aktuellen Mandanten werden als
                ZIP-Datei vorbereitet.
              </Typography>
            </Box>
          </Stack>

          <IconButton
            aria-label="Schließen"
            onClick={onClose}
            disabled={isWorking}
          >
            <Iconify icon="mdi:close" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={3}>
          {!exportStarted && (
            <Alert severity="info">
              Der Export enthält alle QM Ordner und Dateien des aktuellen
              Mandanten. Starten Sie den Export, um die ZIP-Datei vorzubereiten.
            </Alert>
          )}

          <Box
            sx={{
              border: "1px solid",
              borderColor: exportFailed ? "error.light" : "divider",
              borderRadius: 2,
              p: { xs: 2, sm: 3 },
              bgcolor: "background.neutral",
            }}
          >
            <Stack spacing={2.5}>
              <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Box>
                <LinearProgress
                  variant="determinate"
                  color={exportFailed ? "error" : "secondary"}
                  value={progressValue}
                  sx={{ height: 10, borderRadius: 999 }}
                />
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  justifyContent="space-between"
                  sx={{ mt: 1 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {isStarting
                      ? "Export wird gestartet"
                      : exportJob?.message || "Bereit zum Start"}
                    {isPolling ? "..." : ""}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {progressValue}%
                  </Typography>
                </Stack>
              </Box>

              {exportJob && (
                <>
                  <Divider />
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    justifyContent="space-between"
                  >
                    <Typography variant="body2">
                      Dateien:{" "}
                      <strong>
                        {exportJob.fileCount?.toLocaleString("de-DE") || 0}
                      </strong>
                    </Typography>
                    <Typography variant="body2">
                      Größe:{" "}
                      <strong>{formatBytes(exportJob.totalBytes)}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Mandant: <strong>{exportJob.tenantName || "-"}</strong>
                    </Typography>
                  </Stack>
                </>
              )}
            </Stack>
          </Box>

          {exportReady && (
            <Alert severity="success">
              Der Export ist fertig. Die ZIP-Datei kann jetzt heruntergeladen
              werden.
            </Alert>
          )}

          {exportFailed && (
            <Alert severity="error">
              {exportJob?.message || "Der Export konnte nicht erstellt werden."}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={isWorking}>
          Schließen
        </Button>

        {!exportStarted && (
          <Button
            onClick={startExport}
            variant="contained"
            color="secondary"
            startIcon={<Iconify icon="mdi:archive-arrow-down-outline" />}
          >
            Export starten
          </Button>
        )}

        {exportFailed && (
          <Button
            onClick={startExport}
            variant="contained"
            color="secondary"
            startIcon={<Iconify icon="mdi:refresh" />}
          >
            Erneut starten
          </Button>
        )}

        {exportReady && (
          <Button
            onClick={handleDownloadExport}
            disabled={isDownloading}
            variant="contained"
            color="success"
            startIcon={
              <Iconify
                icon={
                  isDownloading
                    ? "eos-icons:loading"
                    : "mdi:download-circle-outline"
                }
              />
            }
          >
            {isDownloading ? "Download..." : "ZIP herunterladen"}
          </Button>
        )}

        {exportStarted && !exportReady && !exportFailed && (
          <Button
            variant="contained"
            disabled
            startIcon={<Iconify icon="eos-icons:loading" />}
          >
            Export läuft
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
